// MainCharts.js

import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip,
} from 'chart.js';
import moment from 'moment';
import { FaExpandAlt } from 'react-icons/fa';
import IntentsPopup from './IntentsPopup';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip);

// Colors configuration object
const chartColors = {
  increase: 'rgba(50, 235, 50, 0.5)',
  decrease: 'rgba(235, 50, 50, 0.5)',
  defaultCategories: 'rgba(99, 102, 241, 0.8)',
  defaultIntents: 'rgba(99, 102, 241, 0.8)',
  //breakoutIntents: 'rgba(232, 121, 249, 0.8)',
  breakoutIntents: 'rgba(79, 70, 229, 0.8)',
  calls: 'rgba(79, 70, 229, 0.6)'
};

// Define the stackedBarLabelsPlugin
const stackedBarLabelsPlugin = {
  id: 'stackedBarLabelsPlugin',
  afterDatasetsDraw(chart) {
    const { ctx, data, scales, options } = chart;
    const percentageChanges = options.plugins.percentageChangePlugin.percentageChanges;

    data.datasets[0].data.forEach((value, index) => {
      const total = data.datasets.reduce((sum, dataset) => sum + dataset.data[index], 0);
      const xStart = scales.x.getPixelForValue(0);
      const xEnd = scales.x.getPixelForValue(total);
      const barWidth = xEnd - xStart;
      const yPos = scales.y.getPixelForValue(data.labels[index]);

      ctx.save();
      ctx.textBaseline = 'middle';
      ctx.font = '12px Lato';

      const percentageChange = parseFloat(percentageChanges[index]);
      const displayText = `${percentageChange.toFixed(1)}%`;

      if (barWidth > 200) {
        ctx.textAlign = 'center';
        ctx.fillStyle = 'white';
        ctx.fillText(displayText, (xStart + xEnd) / 2, yPos);
      } else {
        ctx.textAlign = 'left';
        ctx.fillStyle = 'black';
        ctx.fillText(displayText, xEnd + 5, yPos);
      }

      if (Math.abs(percentageChange) > 20) {
        ctx.font = 'bold 12px Lato';
        ctx.fillText(displayText, barWidth > 200 ? (xStart + xEnd) / 2 : xEnd + 5, yPos);
      }

      ctx.restore();
    });
  },
};

// Define the barLabelsPlugin
const barLabelsPlugin = {
  id: 'barLabelsPlugin',
  afterDatasetsDraw(chart) {
    const { ctx, data, scales } = chart;
    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        const xScale = scales.x;
        const yScale = scales.y;

        const yPos = element.y;
        const xStart = xScale.getPixelForValue(0);
        const xEnd = element.x;
        const barWidth = Math.abs(xEnd - xStart);

        ctx.save();
        ctx.font = '12px Lato';

        const percentageGain = value;
        const currentCount = data.labels[index].currentCount;
        let strValue = `${percentageGain.toFixed(1)}% (${currentCount})`;

        if (barWidth < 40) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'black';
          ctx.fillText(strValue, xEnd + 2, yPos);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'white';
          ctx.fillText(strValue, (xStart + xEnd) / 2, yPos);
        }

        ctx.restore();
      });
    });
  },
};

// Define the simpleValueLabelsPlugin
const simpleValueLabelsPlugin = {
  id: 'simpleValueLabelsPlugin',
  afterDatasetsDraw(chart) {
    const { ctx, data, scales } = chart;
    data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((element, index) => {
        const value = dataset.data[index];
        const xScale = scales.x;
        const yScale = scales.y;

        const yPos = element.y;
        const xStart = xScale.getPixelForValue(0);
        const xEnd = element.x;
        const barWidth = xEnd - xStart;

        ctx.save();
        ctx.font = '12px Lato';

        if (barWidth < 20) {
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'black';
          ctx.fillText(value, xEnd + 2, yPos);
        } else {
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillStyle = 'white';
          ctx.fillText(value, xStart + barWidth / 2, yPos);
        }

        ctx.restore();
      });
    });
  },
};

function MainCharts({
  topCategories = [],
  topIntents = [],
  intentCounts = {},
  baselines = { avgCategoryFrequencies: {}, avgIntentFrequencies: {} },
  dateGroupData = { labels: [], counts: [] },
  dateGroupType = 'day',
  showTrends = false,
  dateRange,
}) {
  const [showIntentsPopup, setShowIntentsPopup] = useState(false);

  if(showTrends && !baselines ) {
    showTrends = false;
  }

  let multiplier;
  switch (dateRange) {
    case "today":
      multiplier = 1;
      break;
    case "thisWeek":
      multiplier = 7;
      break;
    default:
      multiplier = 1;
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
    },
    interaction: {
      mode: 'index',
      intersect: true,
    },
  };

  // Categories chart data and options
  let categoriesChartData;
  let categoriesChartOptions;

  if (showTrends && baselines && 'avgCategoryFrequencies' in baselines) {
    const categories = topCategories.map(item => item.category);
    const baselineData = [];
    const deltaData = [];
    const deltaBackgroundColors = [];
    const percentageChanges = [];
    const totalValues = [];
    const currentCounts = [];
    const baselineCounts = [];

    categories.forEach((category, index) => {
      const currentCount = topCategories[index]?.count || 0;
      const baselineCount = baselines.avgCategoryFrequencies[category] * multiplier || 0;

      const minCount = Math.min(currentCount, baselineCount);
      const change = Math.abs(currentCount - baselineCount);

      baselineData.push(minCount);
      deltaData.push(change);

      const isIncrease = currentCount >= baselineCount;

      deltaBackgroundColors.push(isIncrease ? chartColors.increase : chartColors.decrease);

      let percentageChange = 0;
      if (baselineCount !== 0) {
        percentageChange = ((currentCount - baselineCount) / baselineCount) * 100;
      } else if (currentCount !== 0) {
        percentageChange = 100;
      }

      percentageChanges.push(percentageChange.toFixed(1));
      totalValues.push(minCount + change);
      currentCounts.push(currentCount);
      baselineCounts.push(baselineCount);
    });

    categoriesChartData = {
      labels: categories,
      datasets: [
        {
          label: 'Baseline',
          data: baselineData,
          backgroundColor: chartColors.defaultCategories,
          stack: 'Stack 0',
          tooltip: { enabled: false },
        },
        {
          label: 'Change',
          data: deltaData,
          backgroundColor: deltaBackgroundColors,
          stack: 'Stack 0',
        },
      ],
    };

    categoriesChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: 'Categories',
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          axis: 'y',
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 1;
          },
          callbacks: {
            labelColor: function(context) {
              return {
                borderColor: 'transparent',
                backgroundColor: chartColors.defaultCategories
              };
            },
            label: function (context) {
              const index = context.dataIndex;
              const pluginOptions = context.chart.options.plugins.percentageChangePlugin;
              const currentCount = Math.round(pluginOptions.currentCounts[index]);
              const baselineCount = Math.round(pluginOptions.baselineCounts[index]);
              const percentChange = Math.round(pluginOptions.percentageChanges[index]);
              return [
                `Current: ${currentCount}`,
                `Average: ${baselineCount}`,
                `Change: ${percentChange >= 0 ? '+' : ''}${percentChange}%`
              ];
            },
          },
        },
        percentageChangePlugin: {
          percentageChanges,
          totalValues,
          currentCounts,
          baselineCounts,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count',
          },
        },
        y: {
          stacked: true,
          title: {
            display: false, // Removed y-axis title
            text: 'Categories',
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  } else {
    categoriesChartData = {
      labels: topCategories.map(item => item.category),
      datasets: [
        {
          label: 'Categories',
          data: topCategories.map(item => item.count),
          backgroundColor: topCategories.map(() => chartColors.defaultCategories),
        },
      ],
    };

    categoriesChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: 'Categories',
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count',
          },
        },
        y: {
          title: {
            display: false, // Removed y-axis title
            text: 'Categories',
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  }

  // Intents chart data and options
  let intentsChartData;
  let intentsChartOptions;

  if (showTrends  && baselines &&  'avgIntentFrequencies' in baselines) {
    const intents = topIntents.map(item => item.intent);
    const baselineData = [];
    const deltaData = [];
    const deltaBackgroundColors = [];
    const percentageChanges = [];
    const totalValues = [];
    const currentCounts = [];
    const baselineCounts = [];

    intents.forEach((intent, index) => {
      const currentCount = topIntents[index]?.count  || 0;
      const baselineCount = baselines.avgIntentFrequencies[intent] * multiplier|| 0;

      const minCount = Math.min(currentCount, baselineCount);
      const change = Math.abs(currentCount - baselineCount);

      baselineData.push(minCount);
      deltaData.push(change);

      const isIncrease = currentCount >= baselineCount;

      deltaBackgroundColors.push(isIncrease ? chartColors.increase : chartColors.decrease);

      let percentageChange = 0;
      if (baselineCount !== 0) {
        percentageChange = ((currentCount - baselineCount) / baselineCount) * 100;
      } else if (currentCount !== 0) {
        percentageChange = 100;
      }

      percentageChanges.push(percentageChange.toFixed(1));
      totalValues.push(minCount + change);
      currentCounts.push(currentCount);
      baselineCounts.push(baselineCount);
    });

    intentsChartData = {
      labels: intents,
      datasets: [
        {
          label: 'Baseline',
          data: baselineData,
          backgroundColor: chartColors.defaultIntents,
          stack: 'Stack 0',
          tooltip: { enabled: false },
        },
        {
          label: 'Change',
          data: deltaData,
          backgroundColor: deltaBackgroundColors,
          stack: 'Stack 0',
        },
      ],
    };

    intentsChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: 'Top 10 Intents',
          font: {
            size: 16,
          },
          padding: {
            top: 10,
            bottom: 30
          }
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          axis: 'y',
          filter: function (tooltipItem) {
            return tooltipItem.datasetIndex === 1;
          },
          callbacks: {
            labelColor: function(context) {
              return {
                borderColor: 'transparent',
                backgroundColor: chartColors.defaultIntents // or chartColors.defaultIntents for intents chart
              };
            },
            label: function (context) {
              const index = context.dataIndex;
              const pluginOptions = context.chart.options.plugins.percentageChangePlugin;
              const currentCount = Math.round(pluginOptions.currentCounts[index]);
              const baselineCount = Math.round(pluginOptions.baselineCounts[index]);
              const percentChange = Math.round(pluginOptions.percentageChanges[index]);
              return [
                `Current: ${currentCount}`,
                `Average: ${baselineCount}`,
                `Change: ${percentChange >= 0 ? '+' : ''}${percentChange}%`
              ];
            },
          },
        },
        percentageChangePlugin: {
          percentageChanges,
          totalValues,
          currentCounts,
          baselineCounts,
        },
      },
      scales: {
        x: {
          stacked: true,
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count',
          },
        },
        y: {
          stacked: true,
          title: {
            display: false, // Removed y-axis title
            text: 'Intents',
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  } else {
    intentsChartData = {
      labels: topIntents.map(item => item.intent),
      datasets: [
        {
          label: 'Intents',
          data: topIntents.map(item => item.count),
          backgroundColor: topIntents.map(() => chartColors.defaultIntents),
        },
      ],
    };

    intentsChartOptions = {
      ...chartOptions,
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: 'Top 10 Intents',
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Count',
          },
        },
        y: {
          title: {
            display: false, // Removed y-axis title
            text: 'Intents',
          },
          ticks: {
            autoSkip: false,
          },
        },
      },
    };
  }

  // Breakout Intents chart data and options
  let breakoutIntentsChartData = null;
  let breakoutIntentsChartOptions = null;

  if (showTrends  && baselines &&  'avgIntentFrequencies' in baselines) {
    const intentsData = [];
    for (const intent in intentCounts) {
      const currentCount = intentCounts[intent] || 0;
      const averageCount = baselines.avgIntentFrequencies[intent] * multiplier || 0;
  
      let percentageGain = 0;
      if (averageCount !== 0) {
        percentageGain = ((currentCount - averageCount) / averageCount) * 100;
      } else if (currentCount !== 0) {
        percentageGain = 100;
      }
      intentsData.push({
        intent,
        percentageGain,
        currentCount,
        averageCount
      });
    }
  
    intentsData.sort((a, b) => b.percentageGain - a.percentageGain);
  
    const topBreakoutIntents = intentsData.slice(0, 10);

    const currentCounts = topBreakoutIntents.map((item) => item.currentCount);
    const averageCounts = topBreakoutIntents.map((item) => item.averageCount);
    const percentageGains = topBreakoutIntents.map((item) => item.percentageGain);
  
    breakoutIntentsChartData = {
      labels: topBreakoutIntents.map((item) => ({
        label: item.intent,
        currentCount: item.currentCount
      })),
      datasets: [
        {
          label: 'Percentage Gain',
          data: topBreakoutIntents.map((item) => item.percentageGain),
          backgroundColor: chartColors.breakoutIntents,
        },
      ],
    };
  
    breakoutIntentsChartOptions = {
      ...chartOptions,
      indexAxis: 'y',
      plugins: {
        ...chartOptions.plugins,
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: 'Breakout Intents',
        },
        tooltip: {
          mode: 'nearest',
          intersect: false,
          axis: 'y',
          callbacks: {
            title: function(context) {
              const index = context[0].dataIndex;
              const data = topBreakoutIntents[index];
              return data.intent;
            },
            label: function(context) {
              const index = context.dataIndex;
              const data = topBreakoutIntents[index];
              const percentageGain = data.percentageGain.toFixed(1);
              const currentCount = data.currentCount;
              const averageCount = data.averageCount.toFixed(1);
              return [
                `Current: ${currentCount}`,
                `Average: ${averageCount}`,
                `Change: ${percentageGain >= 0 ? '+' : ''}${percentageGain}%`
              ];
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Percentage Gain',
          },
        },
        y: {
          title: {
            display: false,
            text: 'Intents',
          },
          ticks: {
            autoSkip: false,
            callback: function(value, index) {
              return this.getLabelForValue(value).label;
            }
          },
        },
      },
    };
  }

  // Calls chart for showTrends = false
  let callsChartData = null;
  let callsChartOptions = null;

  if (!showTrends && dateGroupData && dateGroupData.labels && dateGroupData.labels.length > 0) {
    callsChartData = {
      labels: dateGroupData.labels,
      datasets: [
        {
          label: 'Calls',
          data: dateGroupData.counts,
          backgroundColor: chartColors.calls,
        },
      ],
    };

    callsChartOptions = {
      ...chartOptions,
      indexAxis: 'x',
      scales: {
        x: {
          title: {
            display: true,
            text: dateGroupType === 'hour' ? 'Time' : 'Date',
          },
          ticks: {
            autoSkip: false,
            maxRotation: 90,
            minRotation: 45,
            callback: function (value) {
              const label = this.getLabelForValue(value);
              if (dateGroupType === 'hour') {
                return moment(label).format('h:mm A');
              } else {
                return moment(label).format('MM/DD/YYYY');
              }
            },
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Calls',
          },
        },
      },
      plugins: {
        ...chartOptions.plugins,
        title: {
          display: true,
          text: `Calls by ${dateGroupType.charAt(0).toUpperCase() + dateGroupType.slice(1)}`,
        },
      },
    };
  }

  const chartHeight = 350;

  return (
    <div className="flex flex-wrap mb-8">
      <div className="w-full md:w-1/3 p-2" style={{ height: `${chartHeight}px` }}>
        <Bar
          key={`categoriesChart-${showTrends}`}
          options={categoriesChartOptions}
          data={categoriesChartData}
          plugins={showTrends ? [stackedBarLabelsPlugin] : [simpleValueLabelsPlugin]}
        />
      </div>
      <div className="w-full md:w-1/3 p-2" style={{ height: `${chartHeight}px` }}>
        <div className="relative h-full">
          <Bar
            className="h-full"
            key={`intentsChart-${showTrends}`}
            options={intentsChartOptions}
            data={intentsChartData}
            plugins={[
              ...showTrends ? [stackedBarLabelsPlugin] : [simpleValueLabelsPlugin],
            ]}
          />
          <button
            className="absolute top-2 right-2 p-1 text-indigo-500 hover:text-indigo-600 focus:outline-none"
            onClick={() => setShowIntentsPopup(true)}
          >
            <FaExpandAlt className="h-5 w-5" />
          </button>
        </div>
      </div>
      {!showTrends && callsChartData && (
        <div className="w-full md:w-1/3 p-2" style={{ height: `${chartHeight}px` }}>
          <Bar
            options={callsChartOptions}
            data={callsChartData}
          />
        </div>
      )}
      {showTrends && breakoutIntentsChartData && (
        <div className="w-full md:w-1/3 p-2" style={{ height: `${chartHeight}px` }}>
          <Bar
            options={breakoutIntentsChartOptions}
            data={breakoutIntentsChartData}
            plugins={[barLabelsPlugin]}
          />
        </div>
      )}

      {showIntentsPopup && (
        <IntentsPopup
          intentCounts={intentCounts}
          baselines={baselines}
          onClose={() => setShowIntentsPopup(false)}
          baselineMultiplier={multiplier}
        />
      )}
    </div>
  );
}

export default MainCharts;
